import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button, Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/free-solid-svg-icons';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import {
  FONT_GSS_FUTURA_BOOK,
  FONT_GSS_FUTURA_SEMI_BOLD,
} from '@monorepo/shared/src/themes/defaultTheme';
import {
  FileUpload,
  Maybe,
  Student,
} from '@monorepo/shared/src/graph/schema-generated-types-educator';
import FileViewer, {
  fileViewerModalStyles,
} from '@monorepo/shared/src/components/common/FileViewer';
import BaseModalTitle from '@monorepo/shared/src/components/common/Modals/BaseModalTitle';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import useFileUpload from 'state/hooks/use-file-upload';
import { getProgressDataForStudentAndLetter } from '../../utils/reports-util';
import { Character } from '@monorepo/shared/src/graph/schema-generated-types-educator';

const t = (key: string, options?) => translate('reports', key, options);

type Props = {
  student: Student;
  files?: Maybe<FileUpload[]> | any;
  character?: Character | any;
  progressData?: any[];
};

const TurnedInWorkSection: React.FC<Props> = ({ files, student, character, progressData }) => {
  const hasFiles = files?.length;
  const [, { openModal, closeModal }] = useModal();
  const [openFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);
  const progress = getProgressDataForStudentAndLetter(progressData, student?.id, character?.id);

  const noAssignments = !progress;

  const classes = useStyles({ noAssignments });

  const openFileUpload = () => setOpenFileUploadModal(true);

  const handleClose = () => {
    closeModal();
  };

  const viewFilesModal = () => {
    openModal({
      ...fileViewerModalStyles,
      Title: () => <BaseModalTitle text={t('turnedInWork')} onClose={handleClose} />,
      body: (
        <Paper className={classes.modal}>
          <FileViewer files={files} fitToViewport />
        </Paper>
      ),
    });
  };

  useFileUpload({
    student,
    character: character?.character,
    openFileUploadModal,
    handleClose,
    setOpenFileUploadModal,
  });

  return (
    <>
      <Paper variant="outlined" elevation={0} className={classes.root}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={remCalc(12)}>
          <Box>
            <Typography className={classes.title}>{t('studentPracticeBookPageUploads')}</Typography>
            <Typography className={`${classes.summary} noPrint`} style={{ maxWidth: remCalc(657) }}>
              {hasFiles ? t('turnedInWorkSummary') : t('noWorkSummary')}
            </Typography>
            <Typography className={`${classes.summary} printOnly`}>
              {hasFiles
                ? t('turnedInWorkSummaryPrint', { count: hasFiles })
                : t('noWorkSummaryPrint')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <Button
              color="primary"
              onClick={hasFiles ? viewFilesModal : openFileUpload}
              disabled={noAssignments}
              className="noPrint"
            >
              <FontAwesomeIcon className={classes.buttonLinkIcon} icon={faFile} />
              <Typography color="primary" className={classes.buttonLinkText}>
                {hasFiles ? t('viewTurnedInWork') : t('turnInWork')}
              </Typography>
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: `${theme.spacing(3)} ${theme.spacing(6)}`,
  },
  title: {
    fontSize: remCalc(18),
    lineHeight: remCalc(28),
    marginBottom: remCalc(8),
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
    color: (props: { noAssignments: boolean }) =>
      props.noAssignments ? theme.palette.grey[400] : theme.palette.black.main,
  },
  summary: {
    fontSize: remCalc(18),
    lineHeight: remCalc(28),
    fontFamily: FONT_GSS_FUTURA_BOOK,
    color: (props: { noAssignments: boolean }) =>
      props.noAssignments ? theme.palette.grey[400] : theme.palette.black.main,
  },
  checkboxLabel: {
    fontSize: remCalc(16),
    lineHeight: remCalc(24),
    fontFamily: FONT_GSS_FUTURA_BOOK,
  },
  buttonLinkIcon: {
    fontSize: remCalc(16),
    marginRight: remCalc(10),
  },
  buttonLinkText: {
    fontSize: remCalc(14),
    lineHeight: remCalc(20),
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
    color: (props: { noAssignments: boolean }) =>
      props.noAssignments ? theme.palette.grey[400] : theme.palette.link.main,
  },
  modal: {
    background: theme.palette.background.paper,
    marginTop: remCalc(-10),
    height: 'max-content',
    minWidth: remCalc(400),
  },
}));

export default TurnedInWorkSection;
